import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import { supabase } from "@/integrations/supabase/client";
import type { PlanInfo, UserPlan } from "@/types/billing";

const TRIAL_DAYS = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parseUserPlan(raw: unknown): UserPlan {
  return raw === "pro" ? "pro" : "free";
}

function trialEndsFromStarted(started: Date): Date {
  const d = new Date(started);
  d.setDate(d.getDate() + TRIAL_DAYS);
  return d;
}

const GUEST_PLAN: PlanInfo = {
  plan: "free",
  isTrialActive: false,
  trialEndsAt: null,
  trialStartedAt: null,
  daysLeftInTrial: null,
  canUseProFeatures: false,
  canStartTrial: false,
};

/** Logika planu — eksportowana do testów jednostkowych. */
export function planInfoFromProfileRow(opts: {
  plan: UserPlan;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  isLoggedIn: boolean;
}): PlanInfo {
  const { plan, trial_started_at, trial_ends_at, isLoggedIn } = opts;

  if (!isLoggedIn) {
    return GUEST_PLAN;
  }

  if (plan === "pro") {
    return {
      plan: "pro",
      isTrialActive: false,
      trialEndsAt: null,
      trialStartedAt: trial_started_at ? new Date(trial_started_at) : null,
      daysLeftInTrial: null,
      canUseProFeatures: true,
      canStartTrial: false,
    };
  }

  const trialStartedAt = trial_started_at ? new Date(trial_started_at) : null;
  let trialEndsAt = trial_ends_at ? new Date(trial_ends_at) : null;
  if (trialStartedAt && !trialEndsAt) {
    trialEndsAt = trialEndsFromStarted(trialStartedAt);
  }

  const now = Date.now();
  const isTrialActive = Boolean(
    trialStartedAt &&
      trialEndsAt &&
      now < trialEndsAt.getTime() &&
      now >= trialStartedAt.getTime()
  );

  let daysLeftInTrial: number | null = null;
  if (isTrialActive && trialEndsAt) {
    daysLeftInTrial = Math.max(0, Math.ceil((trialEndsAt.getTime() - now) / MS_PER_DAY));
  }

  const canUseProFeatures = isTrialActive;
  const canStartTrial = !trialStartedAt;

  return {
    plan: "free",
    isTrialActive,
    trialEndsAt,
    trialStartedAt,
    daysLeftInTrial,
    canUseProFeatures,
    canStartTrial,
  };
}

export async function startTrial(
  client: SupabaseClient<Database>,
  userId: string
): Promise<{ error?: string }> {
  const { data, error: selErr } = await client
    .from("profiles")
    .select("trial_started_at")
    .eq("id", userId)
    .maybeSingle();

  if (selErr) {
    return { error: selErr.message };
  }
  if (data?.trial_started_at) {
    return { error: "Trial został już wcześniej uruchomiony." };
  }

  const now = new Date();
  const ends = trialEndsFromStarted(now);

  const { error } = await client
    .from("profiles")
    .update({
      plan: "free",
      trial_started_at: now.toISOString(),
      trial_ends_at: ends.toISOString(),
    })
    .eq("id", userId);

  if (error) {
    return { error: error.message };
  }
  return {};
}

export interface UsePlanInfoResult extends PlanInfo {
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function usePlanInfo(): UsePlanInfoResult {
  const { user, loading: authLoading } = useAuth();
  const [info, setInfo] = useState<PlanInfo>(GUEST_PLAN);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setInfo(GUEST_PLAN);
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("profiles")
      .select("plan, trial_started_at, trial_ends_at")
      .eq("id", user.id)
      .maybeSingle();

    if (error || !data) {
      setInfo(
        planInfoFromProfileRow({
          plan: "free",
          trial_started_at: null,
          trial_ends_at: null,
          isLoggedIn: true,
        })
      );
      setLoading(false);
      return;
    }

    setInfo(
      planInfoFromProfileRow({
        plan: parseUserPlan(data.plan),
        trial_started_at: data.trial_started_at,
        trial_ends_at: data.trial_ends_at,
        isLoggedIn: true,
      })
    );
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (authLoading) {
      return;
    }
    void refresh();
  }, [authLoading, refresh]);

  return {
    ...info,
    isLoading: authLoading || loading,
    refresh,
  };
}
